import React, { useState, useEffect, lazy, Suspense, memo, useRef } from 'react';
import { Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import useAdminAuth from '../../../hooks/admin/useAdminAuth';
import { auth } from '../../../firebase';
import { signOut } from 'firebase/auth';

// 모바일 환경 감지 헬퍼 함수
const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 1024 || 
    Boolean(navigator.userAgent.match(/Android/i)) || 
    Boolean(navigator.userAgent.match(/iPhone|iPad|iPod/i));
};

// 사이드바 컴포넌트 지연 로딩 (에러 경계 추가)
const Sidebar = lazy(() => 
  import('./Sidebar').catch(() => {
    console.error('사이드바 로딩 실패');
    // 에러 발생 시 빈 컴포넌트 반환
    return { default: memo(({ handleLogout, isDesktopCollapsed, isMobile }: { 
      handleLogout: () => void; 
      isDesktopCollapsed?: boolean; 
      isMobile?: boolean; 
    }) => (
      <div className={`${!isMobile && isDesktopCollapsed ? 'w-16' : 'w-64'} bg-white shadow-lg p-4 transition-all duration-250`}>
        <h1 className={`text-xl font-bold text-gray-800 ${!isMobile && isDesktopCollapsed ? 'hidden' : ''}`}>관리자 페이지</h1>
        <div className="mt-8">
          <button onClick={handleLogout} className="text-red-500">
            {!isMobile && isDesktopCollapsed ? '로' : '로그아웃'}
          </button>
        </div>
      </div>
    ))}
  })
);

// 메모이제이션된 로딩 컴포넌트
const LoadingFallback = memo(({ isCollapsed = false }: { isCollapsed?: boolean }) => (
  <div className={`${isCollapsed ? 'w-16' : 'w-64'} bg-white shadow-lg h-full animate-pulse transition-all duration-250`}>
    <div className="p-4">
      <div className={`h-6 bg-gray-200 rounded ${isCollapsed ? 'w-8' : 'w-3/4'}`}></div>
    </div>
    <div className="mt-6 px-4">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className={`h-8 bg-gray-100 rounded my-3 ${isCollapsed ? 'w-8' : 'w-full'}`}></div>
      ))}
    </div>
  </div>
));



const AdminLayout: React.FC = () => {
  const { loading, user, userLevel, permissions } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(isMobileDevice);
  const sidebarInitialized = useRef(false);

  // 디바운스 처리된 윈도우 리사이즈 감지 
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    // 초기 확인
    checkMobile();
    
    // 리사이즈 이벤트 핸들러 (디바운스 처리)
    let timeoutId: NodeJS.Timeout;
    const handleResize = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(checkMobile, 100);
    };
    
    // 최적화된 이벤트 리스너
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 페이지 변경 시 모바일에서 사이드바 닫기
  useEffect(() => {
    if (isMobile && isSidebarOpen) {
      setIsSidebarOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isMobile]);

  // 로딩 실패 백업 처리
  useEffect(() => {
    if (!sidebarInitialized.current) {
      const timer = setTimeout(() => {
        setIsMobile(isMobileDevice());
        sidebarInitialized.current = true;
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/admin/signin');
    } catch (error) {
      console.error('로그아웃 실패:', error);
    }
  };

  // 로딩 중일 때의 UI
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">로딩 중...</div>
      </div>
    );
  }

  // 사용자가 없거나 관리자가 아닌 경우 리다이렉트
  if (!user || !userLevel || userLevel > 2) {
    return <Navigate to="/admin/signin" replace />;
  }

  // 최고관리자는 모든 메뉴에 접근 가능
  const isSuperAdmin = userLevel === 1;

  // 사이드바 권한 설정
  const sidebarPermissions = permissions || {};
  
  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {/* 모바일 오버레이 */}
      {isMobile && isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black z-20 will-change-opacity"
          style={{ 
            transition: 'opacity 200ms ease-in-out',
            opacity: 0.5,
            pointerEvents: 'auto',
          }}
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
      
      {/* 사이드바 (항상 렌더링하지만 화면 밖으로 밀어냄) */}
      <Suspense fallback={<LoadingFallback isCollapsed={!isMobile && isDesktopSidebarCollapsed} />}>
        <Sidebar 
          isOpen={isSidebarOpen} 
          onClose={() => setIsSidebarOpen(false)}
          isMobile={isMobile}
          isDesktopCollapsed={isDesktopSidebarCollapsed}
          isSuperAdmin={isSuperAdmin}
          permissions={sidebarPermissions}
          handleLogout={handleLogout}
          user={user}
          userLevel={userLevel}
        />
      </Suspense>

      {/* 메인 콘텐츠 */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* 데스크탑 토글 버튼 */}
        {!isMobile && (
          <div className="bg-white border-b p-4">
            <button
              onClick={() => setIsDesktopSidebarCollapsed(!isDesktopSidebarCollapsed)}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              aria-label={isDesktopSidebarCollapsed ? "사이드바 펼치기" : "사이드바 접기"}
              type="button"
            >
              <svg
                className={`w-6 h-6 transition-transform duration-200 ${isDesktopSidebarCollapsed ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                />
              </svg>
            </button>
          </div>
        )}
        
        {/* 페이지 콘텐츠 */}
        <main className="flex-1 overflow-auto" data-admin-scroll-container>
          <div className="p-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <Outlet />
            </div>
          </div>
        </main>
      </div>

      {/* 모바일 플로팅 햄버거 버튼 */}
      {isMobile && !isSidebarOpen && (
        <button
          onClick={() => setIsSidebarOpen(true)}
          className="fixed top-4 left-4 z-50 bg-white shadow-lg rounded-full p-3 text-gray-600 hover:text-gray-800 hover:shadow-xl transition-all duration-200"
          aria-label="메뉴 열기"
          type="button"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default AdminLayout;
